import { Type } from '@angular/core';
import esriBasemap from '@arcgis/core/Basemap';
import type { BasemapProperties } from "@arcgis/core/Basemap";
import {
  property,
  subclass
} from '@arcgis/core/core/accessorSupport/decorators';
import esriElevationLayer from '@arcgis/core/layers/ElevationLayer';
import type { ElevationLayerProperties } from "@arcgis/core/layers/ElevationLayer";
import esriFeatureLayer from '@arcgis/core/layers/FeatureLayer';
import type { FeatureLayerProperties } from "@arcgis/core/layers/FeatureLayer";
import esriGeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import type { GeoJSONLayerProperties } from "@arcgis/core/layers/GeoJSONLayer";
import esriGraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type { GraphicsLayerProperties } from "@arcgis/core/layers/GraphicsLayer";
import esriGroupLayer from '@arcgis/core/layers/GroupLayer';
import type { GroupLayerProperties } from "@arcgis/core/layers/GroupLayer";
import esriLayer from '@arcgis/core/layers/Layer';
import type { LayerProperties } from "@arcgis/core/layers/Layer";
import esriMapImageLayer from '@arcgis/core/layers/MapImageLayer';
import type { MapImageLayerProperties } from "@arcgis/core/layers/MapImageLayer";
import esriTileLayer from '@arcgis/core/layers/TileLayer';
import type { TileLayerProperties } from "@arcgis/core/layers/TileLayer";
import esriVectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import type { VectorTileLayerProperties } from "@arcgis/core/layers/VectorTileLayer";
import esriWebTileLayer from '@arcgis/core/layers/WebTileLayer';
import type { WebTileLayerProperties } from "@arcgis/core/layers/WebTileLayer";
import esriWMTSLayer from '@arcgis/core/layers/WMTSLayer';
import type { WMTSLayerProperties } from "@arcgis/core/layers/WMTSLayer";
import WebMap from '@arcgis/core/WebMap';
import WebScene from '@arcgis/core/WebScene';
import {
  BaseJepTools,
  BaseLayerInterface,
  BaseLayerMixin,
  BaseLayerProperties
} from './base-layer.mixin';
import { ThemeColor } from '../../jmkw/theme-color.type';
import Collection from '@arcgis/core/core/Collection';
import { JepToolsLayerInterface } from './jep-tools.mixin';
import type { GraphicHit, ViewHitTestResult, ViewHit, HitTestOptions } from "@arcgis/core/views/types";
import type Graphic from "@arcgis/core/Graphic";
import type { ClickEvent } from "@arcgis/core/views/input/types";
import type MapView from "@arcgis/core/views/MapView";
import type { MapViewProperties } from "@arcgis/core/views/MapView";
import type { ConstraintsProperties } from "@arcgis/core/views/3d/constraints/Constraints";
import type View2DConstraints from "@arcgis/core/views/2d/MapViewConstraints";
import type { SceneViewProperties } from "@arcgis/core/views/SceneView";
import type { MapProperties } from "@arcgis/core/Map";

// Layer class definitions
export type JepMapImageLayerProperties = MapImageLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.MapImageLayer')
export class JepMapImageLayer extends BaseLayerMixin<Type<esriMapImageLayer>>(
  esriMapImageLayer
) {}

export type JepFeatureLayerProperties = FeatureLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.FeatureLayer')
export class JepFeatureLayer extends BaseLayerMixin<Type<esriFeatureLayer>>(
  esriFeatureLayer
) {}

export type JepWebTileLayerProperties = WebTileLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.WebTileLayer')
export class JepWebTileLayer extends BaseLayerMixin<Type<esriWebTileLayer>>(
  esriWebTileLayer
) {}

export type JepWMTSLayerProperties = WMTSLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.WMTSLayer')
export class JepWMTSLayer extends BaseLayerMixin<Type<esriWMTSLayer>>(
  esriWMTSLayer
) {}

export type JepElevationLayerProperties = ElevationLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.ElevationLayer')
export class JepElevationLayer extends BaseLayerMixin<Type<esriElevationLayer>>(
  esriElevationLayer
) {}

export type JepVectorTileLayerProperties = VectorTileLayerProperties &
  BaseLayerProperties;

@subclass('jmkw.layers.VectorTileLayer')
export class JepVectorTileLayer extends BaseLayerMixin<
  Type<esriVectorTileLayer>
>(esriVectorTileLayer) {}

export type JepGroupLayerProperties = GroupLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.JepGroupLayer')
export class JepGroupLayer extends BaseLayerMixin(esriGroupLayer) {
  constructor(properties?: JepGroupLayerProperties) {
    super(properties);
  }

  /**
   * Override the layers property to specify it contains BaseLayerInterface compatible layers
   */
  @property()
  override get layers(): Collection<
    esriLayer & BaseLayerInterface & JepToolsLayerInterface<BaseJepTools>
  > {
    return super.layers as Collection<
      esriLayer & BaseLayerInterface & JepToolsLayerInterface<BaseJepTools>
    >;
  }

  /**
   * Add a layer to the group, ensuring it's properly typed
   * @param layer The layer to add to the group
   */
  addLayer(
    layer: esriLayer & BaseLayerInterface & JepToolsLayerInterface<BaseJepTools>
  ): void {
    this.add(layer);
  }
}

export type JepGeoJSONLayerProperties = GeoJSONLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.GeoJSONLayer')
export class JepGeoJSONLayer extends BaseLayerMixin<Type<esriGeoJSONLayer>>(
  esriGeoJSONLayer
) {}

export type JepGraphicsLayerProperties = GraphicsLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.GraphicsLayer')
export class JepGraphicsLayer extends BaseLayerMixin<Type<esriGraphicsLayer>>(
  esriGraphicsLayer
) {}

export type JepLayerProperties = LayerProperties & BaseLayerProperties;
@subclass('jmkw.layers.JepLayer')
export class JepLayer extends BaseLayerMixin(esriLayer) {}

export type JepTileLayerProperties = TileLayerProperties &
  BaseLayerProperties;
@subclass('jmkw.layers.TileLayer')
export class JepTileLayer extends BaseLayerMixin<Type<esriTileLayer>>(
  esriTileLayer
) {}

export type JepBasemapProperties = BasemapProperties & {
  theme?: ThemeColor;
};

@subclass('jmkw.basemap.Basemap')
export class JepBasemap extends esriBasemap {
  @property()
  theme: ThemeColor = 'light';

  constructor(props?: JepBasemapProperties) {
    super(props);
    if (props) Object.assign(this, props); // fine, props already typed
  }
}

// Types for map and view
export type JepMapProperties = MapProperties;
export type JepMapViewProperties = MapViewProperties;
export type JepSceneViewProperties = SceneViewProperties;
export type JepMapViewConstraints = View2DConstraints;
export type JepSceneViewConstraintsProperties =
  ConstraintsProperties;

export type JepMap = WebMap & WebScene;
export type EsriViewType = '2D' | '3D';
export interface JepInitialViewProperties
  extends Omit<JepMapViewProperties, 'constraints'>,
    Omit<JepSceneViewProperties, 'constraints'> {
  viewType: EsriViewType;
  visible?: boolean;
  constraints?: Partial<
    JepMapViewConstraints | JepSceneViewConstraintsProperties
  >;
}

export type JepMapView = MapView;
export type JepMapViewHitTestOptions = HitTestOptions;
export type JepViewClickEvent = ClickEvent;
export type JepViewHit = ViewHit;
export type JepHitTestResult = ViewHitTestResult & {
  event: JepViewClickEvent;
};
export type JepGraphic = Graphic;
export type JepGraphicHit = GraphicHit;
